import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import FormatListBulletedOutlinedIcon from '@mui/icons-material/FormatListBulletedOutlined';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import CodeOutlined from '@mui/icons-material/CodeOutlined';
import InputOutlined from '@mui/icons-material/InputOutlined';
import { Avatar as MUIAvatar, Divider, Typography, Button } from '@mui/material';

import { useAppSelector, useUser } from '../../lib';
import { UserAuthData } from '../../types';
import { Logo } from '../logo';
import { storage } from '../../lib/utils/storage';
import { AppRoutes } from '../../routes';

const navItems = [
  { title: 'nav.campaigns', to: AppRoutes.MAIN.get(), icon: <FormatListBulletedOutlinedIcon /> },
  { title: 'nav.coupons', to: AppRoutes.COUPONS.get(), icon: <ConfirmationNumberOutlinedIcon /> },
  { title: 'nav.account', to: AppRoutes.ACCOUNT.get(), icon: <AccountCircleOutlinedIcon /> },
  { title: 'nav.code', to: AppRoutes.CODE.get(), icon: <CodeOutlined /> },
];

export const MainNav = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const userInfo = useUser();
  const platform = useAppSelector((state) => state.config.platform);

  const user = userInfo.user as UserAuthData;

  const handleLogout = useCallback(() => {
    storage.removeItem('accessToken');
    window.location.replace(AppRoutes.SIGN_IN.get());
  }, []);

  const shouldRenderMenu = () => {
    if (platform !== 'standalone') {
      return false;
    }

    if (
      AppRoutes.EMBED.match(pathname) ||
      AppRoutes.USER_WIDGET.match(pathname) ||
      AppRoutes.USER_WIDGET_WIX.match(pathname) ||
      AppRoutes.SIGN_IN.get() === pathname
    ) {
      return false;
    }

    return true;
  };

  if (!shouldRenderMenu()) {
    return null;
  }

  return (
    <Wrapper>
      <Logo />

      <List>
        {navItems.map(({ title, to, icon }) => (
          <ListItem key={to} current={pathname === to}>
            {icon}

            <Link to={to}>{t(title)}</Link>
          </ListItem>
        ))}
      </List>

      {user && (
        <>
          <Divider />

          <Avatar sizes="40" />

          <Typography fontSize="20px" fontWeight="700" className="user-value">
            {user.name}
          </Typography>

          <Typography fontSize="12px" fontWeight="500" className="user-value">
            {user.email}
          </Typography>

          <LogoutButton endIcon={<InputOutlined />} size="large" onClick={handleLogout}>
            {t('user.exit')}
          </LogoutButton>
        </>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  padding: 24px 12px;
  box-shadow: rgba(0, 0, 0, 0.024) -1px 0px 0px 0px inset;
  width: 240px;
  flex-shrink: 0;
  background-color: #eaeef1;

  .user-value {
    word-break: break-all;
  }
`;

const List = styled.div`
  margin-bottom: 24px;
`;

const ListItem = styled.div<{ current: boolean }>`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  color: #37352f;

  :last-child {
    margin-bottom: 0;
  }

  :hover {
    color: #000;
  }

  ${(p) =>
    p.current &&
    `
      color: #000;
    `};

  a {
    text-decoration: none;
    margin-left: 8px;
    color: #37352f;

    :visited {
      color: #37352f;
    }

    :hover {
      color: #000;
    }

    ${(p) =>
      p.current &&
      `
      color: #000 !important;
      font-weight: 500;
    `};
  }
`;

const Avatar = styled(MUIAvatar)`
  margin-top: 24px;
  margin-bottom: 12px;
`;

const LogoutButton = styled(Button)`
  position: relative;
  left: -8px;
  top: 4px;
`;
